// openEHR LANG model — BMM package org.openehr.lang.bmm.model_access
// Generated by tasks/split_model_by_bmm_packages.ts from tasks/bmm_package_map.json.
// Package boundaries follow the BMM package structure; edit the class bodies here,
// but re-run the splitter rather than hand-moving declarations between packages.

import { BMM_MODEL, BMM_MODEL_METADATA } from "./core/model.ts";
import * as openehr_base from "../../base/mod.ts";

/**
 * Access to BMM models that have been loaded and validated from one or more schema sets.
 */
export class BMM_MODEL_ACCESS {
  /**
   * List of directories where all the schemas loaded here are found.
   */
  schema_directories?: string[];
  /**
   * All schemas found and loaded from \`_schema_directory_\`. Keyed by \`_schema_id_\`.
   */
  all_schemas?: Map<string, BMM_SCHEMA_DESCRIPTOR>;
  /**
   * Top-level (root) models in use, keyed by \`_model_id_\`.
   */
  bmm_models?: Map<string, BMM_MODEL>;
  /**
   * Validated models, keyed by \`_model_id()_\` and any shorter forms of id, with some or no versioning information. For example, the keys \`"openEHR_EHR_1.0.4"\`, \`"openEHR_EHR_1.0"\`, \`"openEHR_EHR_1"\`, and \`"openEHR_EHR"\` will all match the \`"openEHR_EHR_1.0.4"\` model, assuming it is the most recent version available.
   */
  matching_bmm_models?: Map<string, BMM_MODEL>;

  constructor() {
    this.schema_directories = [];
    this.all_schemas = new Map();
    this.bmm_models = new Map();
    this.matching_bmm_models = new Map();
  }

  /**
   * Initialise with a specific schema load list, usually a sub-set of schemas that will be found in a specified directories \`_a_schema_dirs_\`.
   * @param a_schema_dirs - Parameter
   * @param a_schema_load_list - Parameter
   * @returns Result value
   */
  initialise_with_load_list(
    a_schema_dirs: string[],
    a_schema_load_list: string[],
  ): void {
    // Initialize with the specified directories
    this.schema_directories = a_schema_dirs || [];
    // Clear existing schemas and reload with the load list
    this.all_schemas = new Map();
    this.bmm_models = new Map();
    this.matching_bmm_models = new Map();
    // Note: Actual schema loading from files would require file system access
    // This is a stub that sets up the structures for in-memory use
  }

  /**
   * Load all schemas found in a specified directories \`_a_schema_dirs_\`.
   * @param a_schema_dirs - Parameter
   * @returns Result value
   */
  initialise_all(a_schema_dirs: string[]): void {
    // Initialize with the specified directories
    this.schema_directories = a_schema_dirs || [];
    // Clear existing schemas for fresh load
    this.all_schemas = new Map();
    this.bmm_models = new Map();
    this.matching_bmm_models = new Map();
    // Note: Actual schema loading from files would require file system access
  }

  /**
   * Reload BMM schemas.
   * @returns Result value
   */
  reload_schemas(): void {
    // Clear and reload all schemas from configured directories
    this.all_schemas = new Map();
    this.bmm_models = new Map();
    this.matching_bmm_models = new Map();
    // Note: Actual schema reloading would iterate through schema_directories
  }

  /**
   * Return model containing the model key which is a \`_model_id_\` or any shorter form e.g. model id minus the version. If a shorter key is used, the \`BMM_MODEL\` with the most recent version will be selected. Uses \`_matching_bmm_models_\` table to find matches if partial version information is supplied in key.
   * @param a_model_key - Parameter
   * @returns Result value
   */
  bmm_model(a_model_key: openehr_base.String): BMM_MODEL {
    const key = a_model_key?.value || String(a_model_key);
    // First try exact match in bmm_models
    if (this.bmm_models?.has(key)) {
      return this.bmm_models.get(key)!;
    }
    // Then try matching_bmm_models for partial matches
    if (this.matching_bmm_models?.has(key)) {
      return this.matching_bmm_models.get(key)!;
    }
    throw new Error(`BMM model not found for key: ${key}`);
  }

  /**
   * True if a model for a \`_model_key_\` is available. A model key is a \`_model_id_\` or any shorter form e.g. model id minus the version. If a shorter key is used, the Result s True if a \`BMM_MODEL\` with any version exists.
   * @param a_model_key - Parameter
   * @returns Result value
   */
  has_bmm_model(a_model_key: openehr_base.String): openehr_base.Boolean {
    const key = a_model_key?.value || String(a_model_key);
    const exists = (this.bmm_models?.has(key) || false) ||
      (this.matching_bmm_models?.has(key) || false);
    return openehr_base.Boolean.from(exists);
  }
}

/**
 * Descriptor for a BMM schema. Contains a meta-data table of attributes obtained from a mini-ODIN parse of the  schema file.
 */
export abstract class BMM_SCHEMA_DESCRIPTOR {
  /**
   * Persistent form of model.
   */
  bmm_schema?: BMM_SCHEMA;
  /**
   * Computable form of model.
   */
  bmm_model?: BMM_MODEL;
  /**
   * Internal storage for schema_id
   * @protected
   */
  protected _schema_id?: openehr_base.String;

  /**
   * Schema id, formed by
   *
   * \`{BMM_DEFINITIONS}.create_schema_id(
   *   meta_data.item({BMM_DEFINITIONS}.Metadata_model_publisher),
   *   meta_data.item({BMM_DEFINITIONS}.Metadata_schema_name),
   *   meta_data.item({BMM_DEFINITIONS}.Metadata_model_release)\`
   *
   * e.g. \`openehr_rm_1.0.3\`, \`openehr_test_1.0.1\`, \`iso_13606_1_2008_2.1.2\`.
   */
  get schema_id(): string | undefined {
    return this._schema_id?.value;
  }

  /**
   * Gets the openehr_base.String wrapper object for schema_id.
   * Use this to access openehr_base.String methods.
   */
  get $schema_id(): openehr_base.String | undefined {
    return this._schema_id;
  }

  /**
   * Sets schema_id from either a primitive value or openehr_base.String wrapper.
   */
  set schema_id(val: string | openehr_base.String | undefined) {
    if (val === undefined || val === null) {
      this._schema_id = undefined;
    } else if (typeof val === "string") {
      this._schema_id = openehr_base.String.from(val);
    } else {
      this._schema_id = val;
    }
  }

  /**
   * Table of \`{key, value}\` of schema meta-data, keys are string values defined by \`{BMM_DEFINITIONS}.Metadata_*\` constants.
   */
  meta_data?: undefined;
  /**
   * Identifiers of schemas included by this schema.
   */
  includes?: undefined;
  /**
   * True if this is a top-level schema, i.e. is the root schema of a 'model'. True if \`_bmm_schema_ /= Void and then _bmm_schema.model_name_ /= Void\`.
   * @returns Result value
   */
  is_top_level(): openehr_base.Boolean {
    // A schema is top-level if it has a bmm_schema with a model_name
    const hasSchema = this.bmm_schema !== undefined && this.bmm_schema !== null;
    const hasModelName = hasSchema && this.bmm_schema?.model_name !== undefined;
    return openehr_base.Boolean.from(hasSchema && hasModelName);
  }

  /**
   * True if the BMM version found in the schema (or assumed, if none) is compatible with that in this software.
   * @returns Result value
   */
  is_bmm_compatible(): openehr_base.Boolean {
    // Check BMM version compatibility - current supported version is 2.4
    const supportedBmmVersionMajor = 2;
    if (!this.bmm_schema) {
      return openehr_base.Boolean.from(true); // Assume compatible if no schema
    }
    const schemaVersion = this.bmm_schema.bmm_version;
    if (!schemaVersion) {
      return openehr_base.Boolean.from(true); // Assume compatible if no version specified
    }
    // Parse version string to compare major version
    const versionStr = typeof schemaVersion === "string"
      ? schemaVersion
      : (schemaVersion as openehr_base.String).value ?? String(schemaVersion);
    const majorVersion = parseInt(versionStr?.split(".")[0] || "2", 10);
    return openehr_base.Boolean.from(majorVersion <= supportedBmmVersionMajor);
  }

  /**
   * Load schema into in-memory form, i.e. a \`P_BMM_SCHEMA\` instance, if structurally valid. If successful, \`_p_schema_\` will be set.
   * @returns Result value
   */
  load(): void {
    // Abstract loading - actual implementation would parse schema file
    // For now, just ensure the bmm_schema is available
    if (!this.bmm_schema) {
      throw new Error("Cannot load: no BMM schema source available");
    }
  }

  /**
   * Validate loaded schema and report errors.
   * @returns Result value
   */
  validate_merged(): void {
    // Validate the merged schema for structural correctness
    if (!this.bmm_schema) {
      throw new Error("Cannot validate: schema not loaded");
    }
    // Basic validation - check essential properties exist
    // More detailed validation would be implementation-specific
  }

  /**
   * Validate includes list for this schema, to see if each mentioned schema exists in \`_all_schemas_\` list.
   * @param all_schemas_list - Parameter
   * @returns Result value
   */
  validate_includes(
    all_schemas_list: Map<string, BMM_SCHEMA_DESCRIPTOR>,
  ): void {
    // Check each include reference exists in the provided schemas list
    if (!this.includes) {
      return; // No includes to validate
    }
    const includesList = this.includes as unknown as string[];
    for (const includeId of includesList) {
      if (!all_schemas_list.has(includeId)) {
        throw new Error(`Included schema not found: ${includeId}`);
      }
    }
  }

  /**
   * Create \`schema\`, i.e. the \`BMM_MODEL\` from one \`P_BMM_SCHEMA\` schema.
   * @returns Result value
   */
  create_model(): void {
    // Create BMM_MODEL from schema
    if (!this.bmm_schema) {
      throw new Error("Cannot create model: schema not loaded");
    }
    // Create a new BMM_MODEL instance
    this.bmm_model = new BMM_MODEL();
    // Copy essential properties from schema to model
    if (this.bmm_schema.rm_publisher !== undefined) {
      this.bmm_model.rm_publisher = this.bmm_schema.rm_publisher;
    }
    if (this.bmm_schema.rm_release !== undefined) {
      this.bmm_model.rm_release = this.bmm_schema.rm_release;
    }
    if (this.bmm_schema.model_name !== undefined) {
      this.bmm_model.model_name = this.bmm_schema.model_name;
    }
  }
}

/**
 * Abstract parent of any persistable form of a BMM model, e.g. \`P_BMM_SCHEMA\`.
 */
export abstract class BMM_SCHEMA extends BMM_MODEL_METADATA {
  /**
   * Internal storage for bmm_version
   * @protected
   */
  protected _bmm_version?: openehr_base.String;

  /**
   * Version of BMM model, enabling schema evolution reasoning. Persisted attribute.
   */
  get bmm_version(): string | undefined {
    return this._bmm_version?.value;
  }

  /**
   * Gets the openehr_base.String wrapper object for bmm_version.
   * Use this to access openehr_base.String methods.
   */
  get $bmm_version(): openehr_base.String | undefined {
    return this._bmm_version;
  }

  /**
   * Sets bmm_version from either a primitive value or openehr_base.String wrapper.
   */
  set bmm_version(val: string | openehr_base.String | undefined) {
    if (val === undefined || val === null) {
      this._bmm_version = undefined;
    } else if (typeof val === "string") {
      this._bmm_version = openehr_base.String.from(val);
    } else {
      this._bmm_version = val;
    }
  }

  /**
   * Inclusion list of any form of BMM model, in the form of a hash of individual include specifications, each of which at least specifies the id of another schema, and may specify a namespace via which types from the included schemas are known in this schema.
   * Persisted attribute.
   */
  includes?: undefined;
  /**
   * Generated by \`_create_bmm_model_\` from persisted elements.
   */
  bmm_model?: BMM_MODEL;
  /**
   * Current processing state.
   */
  state?: BMM_SCHEMA_STATE;
  /**
   * Internal storage for model_name
   * @protected
   */
  protected _model_name?: openehr_base.String;

  /**
   * Name of this model, if this schema is a model root point. Not set for sub-schemas that are not considered models on their own.
   */
  get model_name(): string | undefined {
    return this._model_name?.value;
  }

  /**
   * Gets the openehr_base.String wrapper object for model_name.
   * Use this to access openehr_base.String methods.
   */
  get $model_name(): openehr_base.String | undefined {
    return this._model_name;
  }

  /**
   * Sets model_name from either a primitive value or openehr_base.String wrapper.
   */
  set model_name(val: string | openehr_base.String | undefined) {
    if (val === undefined || val === null) {
      this._model_name = undefined;
    } else if (typeof val === "string") {
      this._model_name = openehr_base.String.from(val);
    } else {
      this._model_name = val;
    }
  }

  /**
   * Internal storage for schema_name
   * @protected
   */
  protected _schema_name?: openehr_base.String;

  /**
   * Name of model expressed in schema; a 'schema' usually contains all of the packages of one 'model' of a publisher. A publisher with more than one model can have multiple schemas.
   */
  get schema_name(): string | undefined {
    return this._schema_name?.value;
  }

  /**
   * Gets the openehr_base.String wrapper object for schema_name.
   * Use this to access openehr_base.String methods.
   */
  get $schema_name(): openehr_base.String | undefined {
    return this._schema_name;
  }

  /**
   * Sets schema_name from either a primitive value or openehr_base.String wrapper.
   */
  set schema_name(val: string | openehr_base.String | undefined) {
    if (val === undefined || val === null) {
      this._schema_name = undefined;
    } else if (typeof val === "string") {
      this._schema_name = openehr_base.String.from(val);
    } else {
      this._schema_name = val;
    }
  }

  /**
   * Internal storage for schema_revision
   * @protected
   */
  protected _schema_revision?: openehr_base.String;

  /**
   * Revision of schema.
   */
  get schema_revision(): string | undefined {
    return this._schema_revision?.value;
  }

  /**
   * Gets the openehr_base.String wrapper object for schema_revision.
   * Use this to access openehr_base.String methods.
   */
  get $schema_revision(): openehr_base.String | undefined {
    return this._schema_revision;
  }

  /**
   * Sets schema_revision from either a primitive value or openehr_base.String wrapper.
   */
  set schema_revision(val: string | openehr_base.String | undefined) {
    if (val === undefined || val === null) {
      this._schema_revision = undefined;
    } else if (typeof val === "string") {
      this._schema_revision = openehr_base.String.from(val);
    } else {
      this._schema_revision = val;
    }
  }

  /**
   * Internal storage for schema_lifecycle_state
   * @protected
   */
  protected _schema_lifecycle_state?: openehr_base.String;

  /**
   * Schema development lifecycle state.
   */
  get schema_lifecycle_state(): string | undefined {
    return this._schema_lifecycle_state?.value;
  }

  /**
   * Gets the openehr_base.String wrapper object for schema_lifecycle_state.
   * Use this to access openehr_base.String methods.
   */
  get $schema_lifecycle_state(): openehr_base.String | undefined {
    return this._schema_lifecycle_state;
  }

  /**
   * Sets schema_lifecycle_state from either a primitive value or openehr_base.String wrapper.
   */
  set schema_lifecycle_state(val: string | openehr_base.String | undefined) {
    if (val === undefined || val === null) {
      this._schema_lifecycle_state = undefined;
    } else if (typeof val === "string") {
      this._schema_lifecycle_state = openehr_base.String.from(val);
    } else {
      this._schema_lifecycle_state = val;
    }
  }

  /**
   * Internal storage for schema_author
   * @protected
   */
  protected _schema_author?: openehr_base.String;

  /**
   * Primary author of schema.
   */
  get schema_author(): string | undefined {
    return this._schema_author?.value;
  }

  /**
   * Gets the openehr_base.String wrapper object for schema_author.
   * Use this to access openehr_base.String methods.
   */
  get $schema_author(): openehr_base.String | undefined {
    return this._schema_author;
  }

  /**
   * Sets schema_author from either a primitive value or openehr_base.String wrapper.
   */
  set schema_author(val: string | openehr_base.String | undefined) {
    if (val === undefined || val === null) {
      this._schema_author = undefined;
    } else if (typeof val === "string") {
      this._schema_author = openehr_base.String.from(val);
    } else {
      this._schema_author = val;
    }
  }

  /**
   * Internal storage for schema_description
   * @protected
   */
  protected _schema_description?: openehr_base.String;

  /**
   * Description of schema.
   */
  get schema_description(): string | undefined {
    return this._schema_description?.value;
  }

  /**
   * Gets the openehr_base.String wrapper object for schema_description.
   * Use this to access openehr_base.String methods.
   */
  get $schema_description(): openehr_base.String | undefined {
    return this._schema_description;
  }

  /**
   * Sets schema_description from either a primitive value or openehr_base.String wrapper.
   */
  set schema_description(val: string | openehr_base.String | undefined) {
    if (val === undefined || val === null) {
      this._schema_description = undefined;
    } else if (typeof val === "string") {
      this._schema_description = openehr_base.String.from(val);
    } else {
      this._schema_description = val;
    }
  }

  /**
   * Contributing authors of schema.
   */
  schema_contributors?: undefined;
  /**
   * Do some basic validation post initial creation
   *
   * * check that package structure is regular:
   * ** only top-level packages can have qualified names
   * ** no top-level package name can be a direct parent or child of another (child package must be declared under the parent)
   * * check that all classes are mentioned in the package structure
   * * check that all models refer to valid packages
   * @returns Result value
   */
  abstract validate_created(): void;

  /**
   * Finalisation work:
   *
   * * convert packages to canonical form, i.e. full hierarchy with no packages with names like aa.bb.cc
   * * set up include processing list
   * @returns Result value
   */
  abstract load_finalise(): void;

  /**
   * Merge in class and package definitions from \`_other_\`, except where the current schema already has a definition for the given type or package.
   * @param other - Parameter
   * @returns Result value
   */
  abstract merge(other: BMM_SCHEMA): void;

  /**
   * Main validation prior to generation of \`_bmm_model_\`.
   * @returns Result value
   */
  abstract validate(): void;

  /**
   * Populate \`_bmm_model_\` from schema.
   * @returns Result value
   */
  abstract create_bmm_model(): void;

  /**
   * True when validation may be commenced.
   * @returns Result value
   */
  read_to_validate(): openehr_base.Boolean {
    // Ready to validate when essential properties are set
    const hasPublisher = this.rm_publisher !== undefined &&
      this.rm_publisher !== null;
    const hasRelease = this.rm_release !== undefined &&
      this.rm_release !== null;
    const hasSchemaName = this.schema_name !== undefined &&
      this.schema_name !== null;
    return openehr_base.Boolean.from(
      hasPublisher && hasRelease && hasSchemaName,
    );
  }

  /**
   * Identifier of this schema, used for stating inclusions and identifying files. Formed as:
   *
   * \`{BMM_DEFINITIONS}.create_schema_id ( _rm_publisher_,  _schema_name_,   _rm_release_)\`
   *
   * E.g. \`"openehr_rm_ehr_1.0.4"\`.
   * @returns Result value
   */
  schema_id(): openehr_base.String {
    // Form schema ID from component parts
    const publisher = this.rm_publisher || "";
    const schemaName = this.schema_name || "";
    const release = this.rm_release || "";
    return openehr_base.String.from(
      `${publisher.toLowerCase()}_${schemaName.toLowerCase()}_${release}`,
    );
  }
}

/**
 * Schema inclusion structure.
 */
export class BMM_INCLUDE_SPEC {
  /**
   * Internal storage for id
   * @protected
   */
  protected _id?: openehr_base.String;

  /**
   * Full identifier of the included schema, e.g. \`"openehr_primitive_types_1.0.2"\`.
   */
  get id(): string | undefined {
    return this._id?.value;
  }

  /**
   * Gets the openehr_base.String wrapper object for id.
   * Use this to access openehr_base.String methods.
   */
  get $id(): openehr_base.String | undefined {
    return this._id;
  }

  /**
   * Sets id from either a primitive value or openehr_base.String wrapper.
   */
  set id(val: string | openehr_base.String | undefined) {
    if (val === undefined || val === null) {
      this._id = undefined;
    } else if (typeof val === "string") {
      this._id = openehr_base.String.from(val);
    } else {
      this._id = val;
    }
  }
}

export class BMM_SCHEMA_METADATA_KEY extends openehr_base.String {
}

/**
 * Enumeration of processing states of a \`BMM_SCHEMA\` used by creation and validation routines in \`BMM_SCHEMA\`.
 */
export class BMM_SCHEMA_STATE extends openehr_base.String {
}
